import React, { memo, ReactNode } from 'react';
import { Handle, Position } from 'reactflow';

interface N8NStyleNodeProps {
  selected?: boolean;
  icon: ReactNode;
  label: string;
  subtitle?: string;
  color: string;
  handles: {
    source?: boolean;
    target?: boolean;
    sourceHandles?: { id: string; label?: string; position?: 'top' | 'bottom' }[];
  };
}

const N8NStyleNode: React.FC<N8NStyleNodeProps> = memo(({
  selected,
  icon,
  label,
  subtitle,
  color,
  handles,
}) => {
  return (
    <div className="relative">
      {/* Target Handle */}
      {handles.target && (
        <Handle
          type="target"
          position={Position.Top}
          style={{
            width: 12,
            height: 12,
            background: '#fff',
            border: `2px solid ${selected ? color : '#d1d5db'}`,
            top: -6,
          }}
        />
      )}

      {/* Main Node */}
      <div
        className={`
          flex items-center gap-3
          bg-white
          rounded-lg
          px-4 py-3
          min-w-[180px]
          transition-all duration-150
          ${selected 
            ? 'shadow-lg ring-2' 
            : 'shadow-md hover:shadow-lg'
          }
        `}
        style={{
          borderLeft: `4px solid ${color}`,
          boxShadow: selected ? `0 0 0 2px ${color}` : undefined,
        }}
      >
        {/* Icon */}
        <div 
          className="w-10 h-10 rounded-lg flex items-center justify-center text-white text-lg flex-shrink-0"
          style={{ backgroundColor: color }}
        >
          {icon}
        </div>

        {/* Text */}
        <div className="flex-1 min-w-0">
          <div className="text-sm font-semibold text-gray-800 truncate">
            {label}
          </div>
          {subtitle && (
            <div className="text-xs text-gray-500 truncate mt-0.5">
              {subtitle}
            </div>
          )}
        </div>
      </div>

      {/* Source Handle - Default bottom */}
      {handles.source && !handles.sourceHandles && (
        <Handle
          type="source"
          position={Position.Bottom}
          style={{
            width: 12,
            height: 12,
            background: '#fff',
            border: `2px solid ${selected ? color : '#d1d5db'}`,
            bottom: -6,
          }}
        />
      )}

      {/* Multiple Source Handles for conditions */}
      {handles.sourceHandles?.map((handle, index) => {
        const isTrue = handle.label === 'true' || handle.id === 'true';
        const handleColor = isTrue ? '#22c55e' : '#ef4444';
        const totalHandles = handles.sourceHandles?.length || 1;
        const spacing = 100 / (totalHandles + 1);
        const leftPosition = spacing * (index + 1);

        return (
          <div key={handle.id}>
            <Handle
              id={handle.id}
              type="source"
              position={Position.Bottom}
              style={{
                width: 12,
                height: 12,
                background: '#fff',
                border: `2px solid ${handleColor}`,
                bottom: -6,
                left: `${leftPosition}%`,
              }}
            />
            {handle.label && (
              <span 
                className="absolute text-[10px] font-medium"
                style={{ 
                  bottom: -20,
                  left: `${leftPosition}%`,
                  transform: 'translateX(-50%)',
                  color: handleColor,
                }}
              >
                {handle.label === 'true' ? 'Sì' : 'No'}
              </span>
            )}
          </div>
        );
      })}
    </div>
  );
});

N8NStyleNode.displayName = 'N8NStyleNode';

export default N8NStyleNode;
